import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchAllClinics,
  fetchClinicById,
  deleteClinic,
  updateClinic,
  addClinic,
  deleteClinics,
} from '@store/actions/clinic.action'
import { IClinic } from '@dataTypes/clinic.type'

type ClinicsState = {
  data: IClinic[]
  selectedClinic: IClinic | null
  staffAssignedClinicIds: string[] | null
  loading: boolean
  error: string | null
  successMessage: string | null
}

const initialState: ClinicsState = {
  data: [],
  selectedClinic: null,
  staffAssignedClinicIds: null,
  loading: false,
  error: null,
  successMessage: null,
}

const clinicsSlice = createSlice({
  name: 'clinic',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null
    },
    clearSelectedClinic: (state) => {
      state.selectedClinic = null
    },
    setSelectedClinic: (state, action: PayloadAction<IClinic>) => {
      state.selectedClinic = action.payload
      localStorage.setItem('clinic_id', action.payload.clinic_id)
    },
    setStaffAssignedClinicIds: (
      state,
      action: PayloadAction<string[] | null>,
    ) => {
      state.staffAssignedClinicIds = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all clinics
      .addCase(fetchAllClinics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllClinics.fulfilled,
        (state, action: PayloadAction<IClinic[]>) => {
          state.data = action.payload
          state.selectedClinic =
            (() => {
              const clinicId = localStorage.getItem('clinic_id')
              const selectedClinic = clinicId
                ? (action.payload.find(
                    (clinic) => clinic.clinic_id === clinicId,
                  ) ?? null)
                : (action.payload[0] ?? null)
              if (selectedClinic) {
                localStorage.setItem('clinic_id', selectedClinic.clinic_id)
              }
              return selectedClinic
            })() ?? null
          state.loading = false
          state.successMessage = 'Clinics loaded successfully'
        },
      )
      .addCase(fetchAllClinics.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch clinic by ID
    builder
      .addCase(fetchClinicById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchClinicById.fulfilled,
        (state, action: PayloadAction<IClinic>) => {
          state.selectedClinic = action.payload
          state.loading = false
          state.successMessage = 'Clinic loaded successfully'
        },
      )
      .addCase(fetchClinicById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Add clinic
    builder
      .addCase(addClinic.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addClinic.fulfilled, (state) => {
        state.loading = false
        state.successMessage = 'Clinic created successfully'
      })
      .addCase(addClinic.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update clinic
    builder
      .addCase(updateClinic.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updateClinic.fulfilled,
        (state, action: PayloadAction<IClinic>) => {
          const index = state.data.findIndex(
            (clinic) => clinic.clinic_id === action.payload.clinic_id,
          )
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
            }
          }
          // Update selected clinic if it's the same one
          if (state.selectedClinic?.clinic_id === action.payload.clinic_id) {
            state.selectedClinic = {
              ...state.selectedClinic,
              ...action.payload,
            }
          }
          state.loading = false
          state.successMessage = 'Clinic updated successfully'
        },
      )
      .addCase(updateClinic.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to update clinic') as string
      })

    // Delete single clinic
    builder
      .addCase(deleteClinic.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteClinic.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.data = state.data.filter(
            (clinic) => clinic.clinic_id !== action.payload,
          )
          // Clear selected clinic if it was deleted
          if (state.selectedClinic?.clinic_id === action.payload) {
            state.selectedClinic = null
          }
          state.successMessage = 'Clinic removed successfully'
          state.error = null
        },
      )
      .addCase(deleteClinic.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to delete clinic') as string
      })

    // Delete multiple clinics
    builder
      .addCase(deleteClinics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteClinics.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.loading = false
          state.data = state.data.filter(
            (clinic) => !action.payload.includes(clinic.clinic_id),
          )
          // Clear selected clinic if it was deleted
          if (
            state.selectedClinic &&
            action.payload.includes(state.selectedClinic.clinic_id)
          ) {
            state.selectedClinic = null
          }
          state.successMessage = `${action.payload.length} clinic(s) removed successfully`
          state.error = null
        },
      )
      .addCase(deleteClinics.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to delete clinics') as string
      })
  },
})

export const {
  clearError,
  clearSuccessMessage,
  clearSelectedClinic,
  setSelectedClinic,
  setStaffAssignedClinicIds,
} = clinicsSlice.actions

export default clinicsSlice.reducer
